import { createSlice, 
         PayloadAction 
       }                                              from "@reduxjs/toolkit";
import { RootState }                                  from "store/Store";
import { ApiErrorInfo }                               from "types";
import { QA_WORKFLOW, 
         CONTENT_EDIT_STATUS 
       }                                              from "types/cms/frontendTypes";
import { GetQuestionBank }                            from "types/apis/surveyInterfaces";
import { APIData }                                    from "types/cms/frontendTypes";
import { CMS_DEFAULT_LOCALE }                         from "./cmsPilotSlice";

export interface SurveyQBReducerState {
  error              ?: ApiErrorInfo
  categories         : APIData<'api::survey-category.survey-category'>[]
  draftQuestions      : APIData<'api::survey-question.survey-question'>[]
  publishedQuestions  : APIData<'api::survey-question.survey-question'>[]
  modifiedQuestions   : APIData<'api::survey-question.survey-question'>[]
  count : {
    draft : number
    published : number
    modified : number
  }
  unsavedRows: Record<string, Partial<APIData<'api::survey-question.survey-question'>>>;
  hasUnsavedRows: boolean;
  tableState : {
    filters: {
      items: { field: string; operator: string; value: any }[];
    };
    selectedLocale: string;
    showOnlyUnsaved: boolean;
  }
  reviewWorkflow ?: QA_WORKFLOW
}

const defaultState : SurveyQBReducerState = {
  categories : [],
  draftQuestions : [],
  publishedQuestions : [],
  modifiedQuestions : [],
  count : {
    draft : 0,
    published : 0,
    modified : 0,
  },
  unsavedRows: {},
  hasUnsavedRows: false,
  tableState: {
    filters: {
      items: [],
    },
    selectedLocale: CMS_DEFAULT_LOCALE,
    showOnlyUnsaved: false,
  },
}

export const surveyQBSlice = createSlice({
  name : 'surveyQB',
  initialState : defaultState,
  reducers : {

    setQuestionBankAction : (state : SurveyQBReducerState, action : PayloadAction<GetQuestionBank.retval>) => {

      state.categories = action.payload.data.surveyCategories;
      state.draftQuestions = action.payload.data.surveyQuestions.data.draft;
      state.publishedQuestions = action.payload.data.surveyQuestions.data.published;
      state.modifiedQuestions = action.payload.data.surveyQuestions.data.modified;
      state.count = action.payload.data.surveyQuestions.meta.count;
    },

    setQuestionBankError : (state : SurveyQBReducerState, action : PayloadAction<ApiErrorInfo>) => {
      state.error = action.payload
    },

    setUnsavedRowAction: (state: SurveyQBReducerState, action: PayloadAction<{
      id: string;
      diff: Partial<APIData<'api::survey-question.survey-question'>>;
    }>) => {

      state.unsavedRows[action.payload.id] = action.payload.diff;
      state.hasUnsavedRows = true;
    },

    discardUnsavedRowAction: (state: SurveyQBReducerState, action: PayloadAction<{
      id: string;
    }>) => {

      delete state.unsavedRows[action.payload.id];
      if (Object.keys(state.unsavedRows).length === 0) {
        state.hasUnsavedRows = false;
      }
    },

    discardAllUnsavedRowsAction: (state: SurveyQBReducerState) => {
      state.unsavedRows = {};
      state.hasUnsavedRows = false;
    },

    createQuestionAction: (state: SurveyQBReducerState, action: PayloadAction<{
      question : APIData<'api::survey-question.survey-question'>,
    }>) => {

      state.draftQuestions.push(action.payload.question)
      state.count.draft++
    },

    updateModifiedQuestionsAction: (state: SurveyQBReducerState, action: PayloadAction<{
      questions : APIData<'api::survey-question.survey-question'>[],
    }>) => {

      const updatedQuestions = action.payload.questions

      updatedQuestions.forEach((question : APIData<'api::survey-question.survey-question'>) => {
        const docId = question.documentId

        // Remove from unsaved rows
        const unsavedQues = Object.values(state.unsavedRows).find((r) => r.documentId === docId)
        if (unsavedQues) delete state.unsavedRows[unsavedQues.id!]
        
        // Save new recos from backend
        if ((question).editStatus === CONTENT_EDIT_STATUS.DRAFT) {
          const index = state.draftQuestions.findIndex(r => r.documentId === docId)
          if (index !== -1) {
            state.draftQuestions[index] = question
          } else {
            state.draftQuestions.push(question)
            state.count.draft++
            state.draftQuestions.sort((a, b) => (a!.createdAt! > b!.createdAt! ? 1 : -1))
          }
        } else if ((question).editStatus === CONTENT_EDIT_STATUS.MODIFIED) {
          const index = state.modifiedQuestions.findIndex(r => r.documentId === docId)
          if (index !== -1) {
            state.modifiedQuestions[index] = question
          } else {
            state.modifiedQuestions.push(question)
            state.count.modified++
            state.modifiedQuestions.sort((a, b) => (a.createdAt! > b.createdAt! ? 1 : -1))
          }
        } else {
          // Specific disable / enable case
          const index = state.publishedQuestions.findIndex(r => r.documentId === docId)
          if (index >= 0) {
            state.publishedQuestions[index] = question
            recomputeCount(state)
          } 
        }
      })

      if (Object.keys(state.unsavedRows).length === 0) {
        state.hasUnsavedRows = false;
      }
    },

    discardAllDraftQuestionsAction: (state: SurveyQBReducerState, action: PayloadAction<string[]>) => {

      const docIds = action.payload

      for (const docId of docIds) {
        const index = state.modifiedQuestions.findIndex(r => r.documentId === docId)
        if (index !== -1) {
          state.modifiedQuestions.splice(index, 1)
          state.count.modified--
        } else {
          const index = state.draftQuestions.findIndex(r => r.documentId === docId)
          if (index !== -1) {
            state.draftQuestions.splice(index, 1)
            state.count.draft--
          }
        }
      }
    },

    publishQuestionsAction: (state: SurveyQBReducerState, action: PayloadAction<APIData<'api::survey-question.survey-question'>[]>) => {
      
      const publishedQuestions = action.payload
      
      for (const question of publishedQuestions) {

        const index = state.modifiedQuestions.findIndex(r => r.documentId === question.documentId)
        if (index !== -1) {
          state.modifiedQuestions.splice(index, 1)
          state.count.modified--
        } else {
          const index = state.draftQuestions.findIndex(r => r.documentId === question.documentId)
          if (index !== -1) {
            state.draftQuestions.splice(index, 1)
            state.count.draft--
          }
        }

        const existingPublishedQuestion = state.publishedQuestions.findIndex(r => r.documentId === question.documentId)
        if (existingPublishedQuestion !== -1) {
          state.publishedQuestions[existingPublishedQuestion] = question
        } else {
          state.publishedQuestions.push(question)
        }

        state.count.published = state.publishedQuestions.length
      }

      state.publishedQuestions.sort((a, b) => (a.createdAt! > b.createdAt! ? 1 : -1))
    },

    unpublishQuestionsAction: (state: SurveyQBReducerState, action: PayloadAction<APIData<'api::survey-question.survey-question'>[]>) => {
      const draftQuestions = action.payload

      for (const draftQuestion of draftQuestions) {
        const index = state.publishedQuestions.findIndex(r => r.documentId === draftQuestion.documentId)
        if (index !== -1) {
          state.publishedQuestions.splice(index, 1)
          state.count.published--
        }

        state.draftQuestions.push(draftQuestion)
        state.count.draft++
      }

      state.draftQuestions.sort((a, b) => (a.createdAt! > b.createdAt! ? 1 : -1))
    },

    resetSurveyStateAction : (state : SurveyQBReducerState) => {
      state.draftQuestions = [];
      state.publishedQuestions = [];
      state.modifiedQuestions = [];
      state.count = {
        draft : 0,
        published : 0,
        modified : 0,
      };
      state.unsavedRows = {};
      state.hasUnsavedRows = false;
      state.tableState = {
        filters: {
          items: [],
        },
        selectedLocale: CMS_DEFAULT_LOCALE,
        showOnlyUnsaved: false,
      }
    },

    setFiltersAction: (state: SurveyQBReducerState, action: PayloadAction<{ field: string; operator: string; value: any }[]>) => {
      state.tableState.filters.items = action.payload;
    },

    clearFiltersAction: (state: SurveyQBReducerState) => {
      state.tableState.filters.items = [];
    },

    setSelectedLocaleAction: (state: SurveyQBReducerState, action: PayloadAction<string>) => {
      state.tableState.selectedLocale = action.payload;
    },

    setShowOnlyUnsavedAction: (state: SurveyQBReducerState, action: PayloadAction<boolean>) => {
      state.tableState.showOnlyUnsaved = action.payload;
    },

    setReviewWorkflowAction: (state: SurveyQBReducerState, action: PayloadAction<QA_WORKFLOW>) => {
      state.reviewWorkflow = action.payload;
    }
  } as any
}) as any

const recomputeCount = (state : SurveyQBReducerState) => {

  const draftQuestionsCount = state.draftQuestions.length
  const modifiedQuestionsCount = state.modifiedQuestions.length
  const publishedQuestionsCount = state.publishedQuestions.length

  state.count = {
    draft : draftQuestionsCount,
    modified : modifiedQuestionsCount,
    published : publishedQuestionsCount
  }
}

export const {
  setQuestionBankAction,
  resetSurveyStateAction,
  setUnsavedRowAction,
  discardUnsavedRowAction,
  discardAllUnsavedRowsAction,
  createQuestionAction,
  updateModifiedQuestionsAction,
  discardAllDraftQuestionsAction,
  publishQuestionsAction,
  unpublishQuestionsAction,
  setQuestionBankError,
  setFiltersAction,
  clearFiltersAction,
  setSelectedLocaleAction,
  setShowOnlyUnsavedAction,
  setReviewWorkflowAction
} = surveyQBSlice.actions;

export const selectDraftQuestions = (state : RootState) => (state.surveyQB as SurveyQBReducerState).draftQuestions;
export const selectPublishedQuestions = (state : RootState) => (state.surveyQB as SurveyQBReducerState).publishedQuestions;
export const selectModifiedQuestions = (state : RootState) => (state.surveyQB as SurveyQBReducerState).modifiedQuestions;
export const selectCount = (state : RootState) => (state.surveyQB as SurveyQBReducerState).count;
export const selectUnsavedRows = (state : RootState) => (state.surveyQB as SurveyQBReducerState).unsavedRows;
export const selectHasUnsavedRows = (state : RootState) => (state.surveyQB as SurveyQBReducerState).hasUnsavedRows;
export const selectTableState = (state : RootState) => (state.surveyQB as SurveyQBReducerState).tableState;
export const selectShowOnlyUnsaved = (state : RootState) => (state.surveyQB as SurveyQBReducerState).tableState.showOnlyUnsaved;
export const selectReviewWorkflow = (state : RootState) => (state.surveyQB as SurveyQBReducerState).reviewWorkflow;
export const selectSurveyCategories = (state : RootState) => (state.surveyQB as SurveyQBReducerState).categories;